// 注册自定义组件

import { V_FORM_ENUM } from "./enum";
import { generateId } from "./method";
import VForm, { cloneDeep } from "./vform";
import { camelCase, kebabCase, omit, once, isArray, debounce, isFunction } from "lodash-es";
import { vFRDialog, vFRH5Dialog } from "@/CommonDP/pages/mainMg/lowcode/component/vFRDialog.js";
import { openFlow } from "@/CommonDP/components/processComponents/flowNew/index.js";

const { VFormSDK } = VForm;
const CUSTOM_VF_SCHEMA_MAP = new Map();
const utils = { ...VFormSDK, ...V_FORM_ENUM };
const vformFieldList = [...VFormSDK.basicFields, ...VFormSDK.advancedFields];
import { isMobile } from "@/CommonDP/utils";
import Vue from "vue";
import openDynTableDlg from "@/CommonDP/utils/openDynTableDlg";
// 调用vfrom,加载自定义二开组件
export const useVForm = (getList) => {
  Vue.use(VForm);
  setVFormBaseMixins();
  VForm.add(() => {
    const list = getList(utils);
    for (const com of list) {
      addItem(utils, com);
    }
  });
};

// 配合option.setColumns,进行设计提交的数据处理
export const itemColumnsMap = {};
// 配合option.setDesign,进行设计提交的数据处理
export const itemSubmitMap = {};
// 进行自定义事件,准确进行触发,可以通过this.customEvent(eventName,...args) 触发 schema.events注册是事件
export const vformComponentsMap = {};
function setVformComponentsEventParams(type, eventName, args) {
  if (!type || !eventName) return;
  const map = vformComponentsMap[type] || {};
  map[eventName] = args || [];
  vformComponentsMap[type] = map;
}
// 自定义组件集合
function addItem(utils, item) {
  const mobileState = isMobile();
  if (item.h5 && mobileState) { item.component = item.h5; }
  const { group = V_FORM_ENUM.custom, component, renderComponent, label, options, type } = item;
  setSchemaPlus(utils, item);
  const schema = getSimpleSchema(item);
  // 添加必有options
  options.hidden ??= false;
  options.required ??= false;
  // 注册拖拽项
  setWidgetGroup(group, schema);
  if (group === V_FORM_ENUM.container) {
    const designName = type + "-widget";
    const renderName = type + "-item";
    defineVFContainerWidget({ designName, designComponent: component }, { renderName, renderComponent });
  } else {
    const componentName = kebabCase(camelCase(type + "-widget"));
    defineVFFieldWidget(componentName, component);
  }
  // 设置文本
  const cnLang = Vue.prototype.$st("extension.widgetLabel");
  Vue.set(cnLang, item.type, label);
}
function getSimpleSchema(schemaPlus) {
  return omit(schemaPlus, ["label", "group", "component", "advanced", "zh", "getSFC", "setDesign", "props", "events", "renderComponent"]);
}
function setWidgetGroup(group, schema) {
  switch (group) {
    case V_FORM_ENUM.container:
      utils.addContainerWidgetSchema(schema);
      break;
    case V_FORM_ENUM.basic:
      utils.addBasicFieldSchema(schema);
      break;
    case V_FORM_ENUM.advanced:
      utils.addAdvancedFieldSchema(schema);
      break;
    case V_FORM_ENUM.custom:
      utils.addCustomWidgetSchema(schema);
      break;
  }
}
function setSchemaPlus(utils, item) {
  const { type, props: setProps, events: setEvent, setColumns, advanced: setAdvanced, getSFC, zh, setDesign, options } = item;
  // 注册sfc文本方法
  if (getSFC) {
    utils.registerFWGenerator(type, getSFC); // 注册字段组件的代码生成器
  }
  if (zh) {
    const setting = Vue.prototype.$st("extension.setting");
    for (const key of Object.keys(zh)) {
      Vue.set(setting, key, zh[key]);
    }
  }
  // 处理属性设置
  if (setProps) {
    const props = setProps(utils) || {};
    Object.keys(props).forEach((prop) => {
      utils.PERegister.registerCPEditor(`${type}-${prop}`, `${type}-${prop}-editor`, props[prop]);
    });
  }
  if (setAdvanced) {
    const props = setAdvanced(utils) || {};
    Object.keys(props).forEach((prop) => {
      utils.PERegister.registerAPEditor(`${type}-${prop}`, `${type}-${prop}-editor`, props[prop]);
    });
  }
  // 处理事件
  if (setEvent) {
    const events = setEvent(utils) || {};
    Object.keys(events).forEach((name) => {
      options[name] ??= "";
      utils.PERegister.registerEPEditor(`${type}-${name}`, `${type}-${name}-editor`, createEventHandlerEditor(name, events[name] || []));
      setVformComponentsEventParams(type, name, events[name]);
    });
  }
  // 提交设计表单时触发本方法 用于告知后台本组件特殊字段
  if (setDesign) itemSubmitMap[type] = setDesign;
  // 提交设计表单时触发本方法 将增加formColumns提交的字段
  if (setColumns) itemColumnsMap[type] = setColumns;
  // 注册到控件
  CUSTOM_VF_SCHEMA_MAP.set(type, item);
}
function defineVFFieldWidget(name, component) {
  component.componentName ??= "FieldWidget";
  component.name ??= name;
  Reflect.set(VFormSDK.FieldComponents, name, component);
  Vue.component(name, component);
}

function defineVFContainerWidget({ designName, designComponent }, { renderName, renderComponent }) {
  Vue.component(designName, designComponent);
  Vue.component(renderName, renderComponent);
}

/**
 * @description 供生成器使用,创建新的组件
 * @param {string} type
 * @returns {Widget}
 */
export function getCustomWidget(type, data) {
  const sourceSchema = CUSTOM_VF_SCHEMA_MAP.get(type) || vformFieldList.find((item) => item.type === type) || vformFieldList.find((item) => item.type === "input");
  const schema = cloneDeep(sourceSchema);
  Object.assign(schema.options, data);
  schema.options.name = data.columnName || "";
  schema.options.label = data.columnDisplayName || "";
  if (data.defaultValue) {
    schema.options.defaultValue = data.defaultValue;
  }
  const { displayDataType, displayDataLength: length } = data;
  switch (displayDataType) {
    case "input":
    case "textarea":
      if (~~length) {
        schema.options.maxLength = ~~length;
      }
      break;
  }
  schema.options.required = data.isNullable === 0;
  schema.options.hidden = data.isInternalField === 1;
  schema.id = camelCase(type) + generateId();
  return schema;
}
/**
 *
 * @param {string} type 要重写的组件
 * @param {Function(widget,sdk):widget} next 根据旧schema返回新schema
 * @returns {schema} 最后整理之后的schema
 * @description !!警告,本方法极度不稳定,不知道是否会如期运行,所以最好不要修改原组件
 */
export function resetWidget(type, next) {
  const basic_schema = vformFieldList.find((s) => s.type === type);
  const source = cloneDeep(basic_schema);
  const name = `${type}-widget`;
  const component = VFormSDK.FieldComponents[name];
  if (!component) throw new Error(`没有定义${name}组件`);
  source.component = component;
  const plus = next?.(source, utils) || source;
  setSchemaPlus(utils, plus);
  const schema = getSimpleSchema(plus);
  //! 直接修改原组件,为了需求 没有办法,只能修改源对象
  coveringObj(component, { componentName: "FieldWidget", name }, plus.component);
  coveringObj(basic_schema, schema);
  return basic_schema;
}

function coveringObj(source, ...objs) {
  // 循环去掉原对象所有的值
  for (const k of Object.keys(source)) {
    delete source[k];
  }
  // 将新对象所有字段合并过来,生成新的数据
  Object.assign(source, ...objs);
}
/**
 * @description 低代码混合,将应用于所有的vform组件(包含vform自带组件)
 * @type {import("vue").ComponentOptionsMixin}
 */
const lowcodeMixins = {
  inject: {
    getFlowDetail: { from: 'getFlowDetail', default: new Function() },
    extraData: { from: 'extraData', default: () => ({}) }
  },
  methods: {
    openDynTableDlg,
    openFlow,
    vFRDialog,
    vFRH5Dialog
  }
};
/**
 * @description 向`vform组件`添加自定义`method`,供自定义脚本使用
 * __注意:会覆盖VForm组件基础的方法__
 * @param {import("vue").ComponentMethodOptions} methods  添加的方法对象,同`Vue.methods`写法
 */
export function addVFormWidgetMethods(methods) {
  Object.assign(VFormSDK.lowcodeMixins.methods, methods);
}
/**
 * @description  副作用方法: 修改VForm基础mixins,用来调整所有组件效果,慎用
 * */
const setVFormBaseMixins = once(function () {
  VForm.VFormRender.mixins.push(lowcodeMixins);
  Reflect.set(VFormSDK.fieldMixin, 'mixins', [lowcodeMixins]);
  Reflect.set(VFormSDK.containerItemMixin, 'mixins', [lowcodeMixins]);
  // 重写下拉组件文本值的逻辑
  Reflect.set(VFormSDK.fieldMixin.computed, 'optionLabel', function () {
    const { fieldModel } = this;
    const empty = "--";
    if (!fieldModel) {
      return empty;
    } else {
      const { field: { options: { optionItems = [], multiple }}} = this;
      const values = multiple ? isArray(fieldModel) ? fieldModel : fieldModel.split(',') : [fieldModel];
      const str = values.map((value) => optionItems.find((item) => item.value === value)?.label || value).join(',');
      return str || empty;
    }
  });
  // 向fieldMixin添加onField事件 ---
  utils.PERegister.registerEPEditor(`onInitAndUpdate`, `onInitAndUpdate-editor`, createEventHandlerEditor('onInitAndUpdate', ['value']));
  for (const schema of vformFieldList) schema.options.onInitAndUpdate ??= '';
  VFormSDK.fieldMixin.mounted = after(
    VFormSDK.fieldMixin.mounted,
    function () {
      this.onInitAndUpdateHandle(this.fieldModel);
    }
  );
  VFormSDK.fieldMixin.methods.emitFieldDataChange = after(
    VFormSDK.fieldMixin.methods.emitFieldDataChange,
    function (value) {
      this.onInitAndUpdateHandle(value);
    }
  );
  VFormSDK.fieldMixin.computed.onInitAndUpdateHandle = function () {
    const onFieldStr = this.field.options.onInitAndUpdate;
    return onFieldStr ? debounce(new Function('value', onFieldStr).bind(this), 300) : () => void 0;
  };
});
function after(...fns) {
  return function (...args) {
    for (const fn of fns) {
      isFunction(fn) && Reflect.apply(fn, this, args);
    }
  };
}

/**
 * @description 原vform工具方法:定义一个可以在设置界面里,配置方法的组件!!非实例!!
 * @param {string} eventPropName 方法名
 * @param {string[]} eventParams 参数名称列表
 * @returns {import("vue").Component} 表单方法的Component,!!非实例!!
 */
export const createEventHandlerEditor = function (eventPropName, eventParams) {
  return {
    props: {
      optionModel: Object
    },
    mixins: [VFormSDK.emitter],
    methods: {
      editEventHandler() {
        this.dispatch('SettingPanel', 'editEventHandler', [eventPropName, [...eventParams]]);
      }
    },
    computed: {
      eventHandled() {
        return !!this.optionModel[eventPropName];
      },
      buttonClass() {
        return {
          'button-text-highlight': this.eventHandled
        };
      }
    },
    render() {
      const { buttonClass } = this;
      return (
        <el-form-item label={eventPropName} label-width="150px">
          <el-button type="info" icon="el-icon-edit" plain round class={buttonClass} onClick={this.editEventHandler}>
            编写代码
          </el-button>
        </el-form-item>
      );
    }
  };
};
